import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Spinner, Alert } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SlidingMenu from '../components/SlidingMenu';
import VersionFooter from '../components/VersionFooter';
import AlertModal from '../components/AlertModal';
import apiCall from '../shared/apiCall';
import { ApiActions } from '../shared/apiActions';
import { createTripTemplatesV2 } from '../shared/createTripTemplatesV2';
import { convertAiResultToPlan } from '../shared/activityUtils';
import { tryParseJson, fetchAttractionImages } from '../shared/utils';
import { clog, cwarn, cerror } from '../shared/consoleLogger';
import { getCachedAccountData, updateAccountUsage, refreshAccountData } from '../shared/accountCache';

// Comprehensive list of interests
const INTERESTS = [
  { id: 'history', translationKey: 'interestsHistory', icon: 'bi-building' },
  { id: 'museums', translationKey: 'interestsMuseums', icon: 'bi-palette' },
  { id: 'architecture', translationKey: 'interestsArchitecture', icon: 'bi-building' },
  { id: 'waterparks', translationKey: 'interestsWaterparks', icon: 'bi-droplet' },
  { id: 'theme-parks', translationKey: 'interestsThemeParks', icon: 'bi-emoji-smile' },
  { id: 'beaches', translationKey: 'interestsBeaches', icon: 'bi-water' },
  { id: 'nature', translationKey: 'interestsNature', icon: 'bi-tree' },
  { id: 'hiking', translationKey: 'interestsHiking', icon: 'bi-signpost' },
  { id: 'adventure', translationKey: 'interestsAdventure', icon: 'bi-lightning' },
  { id: 'food', translationKey: 'interestsFood', icon: 'bi-cup-hot' },
  { id: 'nightlife', translationKey: 'interestsNightlife', icon: 'bi-moon-stars' },
  { id: 'shopping', translationKey: 'interestsShopping', icon: 'bi-bag' },
  { id: 'spa', translationKey: 'interestsSpa', icon: 'bi-heart' },
  { id: 'photography', translationKey: 'interestsPhotography', icon: 'bi-camera' },
  { id: 'music', translationKey: 'interestsMusic', icon: 'bi-music-note-beamed' },
  { id: 'sports', translationKey: 'interestsSports', icon: 'bi-trophy' },
  { id: 'festivals', translationKey: 'interestsFestivals', icon: 'bi-calendar-event' },
  { id: 'religious', translationKey: 'interestsReligious', icon: 'bi-church' },
  { id: 'zoos', translationKey: 'interestsZoos', icon: 'bi-heart-pulse' },
  { id: 'gardens', translationKey: 'interestsGardens', icon: 'bi-flower1' },
  { id: 'wine', translationKey: 'interestsWine', icon: 'bi-cup-straw' },
  { id: 'casinos', translationKey: 'interestsCasinos', icon: 'bi-dice-6' },
  { id: 'cruises', translationKey: 'interestsCruises', icon: 'bi-water' },
  { id: 'scuba', translationKey: 'interestsScuba', icon: 'bi-water' },
  { id: 'skiing', translationKey: 'interestsSkiing', icon: 'bi-snow' },
  { id: 'golf', translationKey: 'interestsGolf', icon: 'bi-circle' },
  { id: 'family', translationKey: 'interestsFamily', icon: 'bi-people' },
  { id: 'romantic', translationKey: 'interestsRomantic', icon: 'bi-heart-fill' },
  { id: 'budget', translationKey: 'interestsBudget', icon: 'bi-wallet2' },
  { id: 'luxury', translationKey: 'interestsLuxury', icon: 'bi-gem' }
];

export default function CreateTripV2() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();  
  
  // Destination state - free text
  const [destination, setDestination] = useState('');
  
  // Group members state
  const [groupMembers, setGroupMembers] = useState({
    adults: 0,
    kids: 0,
    babies: 0,
    seniors: 0
  });
  
  // Interests state
  const [selectedInterests, setSelectedInterests] = useState([]);
  
  // Dates state
  const [overallDates, setOverallDates] = useState({
    startDate: '',
    endDate: ''
  });
  
  // Validation errors
  const [errors, setErrors] = useState({});
  
  // Loading and result states
  const [isLoading, setIsLoading] = useState(false);
  const [loadingButton, setLoadingButton] = useState(null); // 'empty' or 'ai' or null
  const [currentStep, setCurrentStep] = useState(''); // Progress message
  const [error, setError] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [alertVariant, setAlertVariant] = useState('error'); // 'error' | 'info' | 'warning' | 'success'
  const [showLanguageDisclaimer, setShowLanguageDisclaimer] = useState(false);

  // Check if language disclaimer should be shown
  useEffect(() => {
    const checkLanguageDisclaimer = async () => {
      // Only show for non-English languages
      if (i18n.language === 'en') {
        return;
      }

      // Check if disclaimer has been shown before
      const account = await getCachedAccountData();
      if (account?.usage?.languageDisclaimerShown) {
        return;
      }

      // Show the disclaimer
      setShowLanguageDisclaimer(true);
    };

    checkLanguageDisclaimer();
  }, [i18n.language]);

  // Handle dismissing the language disclaimer
  const handleDismissLanguageDisclaimer = async () => {
    setShowLanguageDisclaimer(false);
    
    // Update account to mark disclaimer as shown (this also clears the cache)
    await updateAccountUsage({ languageDisclaimerShown: true });
  };

  // Handle group member changes
  const handleGroupMemberChange = (type, value) => {
    // Allow empty string for better UX (user can clear the field)
    if (value === '' || value === null || value === undefined) {
      setGroupMembers(prev => ({
        ...prev,
        [type]: 0
      }));
      return;
    }
    
    // Remove any non-numeric characters
    let cleanedValue = value.toString().replace(/[^0-9]/g, '');
    
    // Remove leading zeros (but keep at least one digit if the value was all zeros)
    if (cleanedValue.length > 1) {
      cleanedValue = cleanedValue.replace(/^0+/, '') || '0';
    }
    
    const numValue = parseInt(cleanedValue) || 0;
    if (numValue < 0) return;
    
    setGroupMembers(prev => ({
      ...prev,
      [type]: numValue
    }));
  };

  // Handle interest toggle
  const toggleInterest = (interestId) => {
    setSelectedInterests(prev => {
      if (prev.includes(interestId)) {
        return prev.filter(id => id !== interestId);
      } else {
        return [...prev, interestId];
      }
    });
  };

  // Build group info string from form data
  const buildGroupInfoString = () => {
    const parts = [];
    const total = groupMembers.adults + groupMembers.kids + groupMembers.babies + groupMembers.seniors;
    
    if (total === 0) {
      return '1 adult'; // Default
    }
    
    if (groupMembers.adults > 0) {
      parts.push(`${groupMembers.adults} adult${groupMembers.adults > 1 ? 's' : ''}`);
    }
    if (groupMembers.kids > 0) {
      parts.push(`${groupMembers.kids} kid${groupMembers.kids > 1 ? 's' : ''} (3-18)`);
    }
    if (groupMembers.babies > 0) {
      parts.push(`${groupMembers.babies} bab${groupMembers.babies > 1 ? 'ies' : 'y'}/toddler${groupMembers.babies > 1 ? 's' : ''} (0-3)`);
    }
    if (groupMembers.seniors > 0) {
      parts.push(`${groupMembers.seniors} senior${groupMembers.seniors > 1 ? 's' : ''} (65+)`);
    }
    
    return parts.join(', ');
  };

  // Build interests string from selected interests
  const buildInterestsString = () => {
    if (selectedInterests.length === 0) return '';
    
    const interestLabels = selectedInterests.map(id => {
      const interest = INTERESTS.find(i => i.id === id);
      return interest ? t(`tripCreator.${interest.translationKey}`) : id;
    });
    
    return `Interests: ${interestLabels.join(', ')}`;
  };

  // Validate destination by checking if it contains trip locations
  const validateDestination = async (destinationText) => {
    try {
      const response = await apiCall('/api/rawAi', {
        prompt: destinationText,
        context: createTripTemplatesV2.preUserDataCheck,
        systemContent: createTripTemplatesV2.systemContext.getTripPlannerAssistant(i18n.language),
        feature: 'create_trip_destination_validation',
        language: i18n.language
      }, 'POST');

      if (!response.success) {
        const errorMsg = `Validation failed: ${response.message || 'Failed to validate destination'}\n\nAI Response: ${response.rawContent || 'No response'}`;
        setError(errorMsg);
        setShowErrorModal(true);
        return { found: false, rawResponse: response.rawContent };
      }

      const { jsonData, result } = tryParseJson(response.rawContent);
      if (!result || !jsonData || !jsonData.tripLocations) {
        const errorMsg = `Invalid validation response.\n\nAI Raw Response: ${response.rawContent}\n\nParsed Result: ${JSON.stringify({ jsonData, result }, null, 2)}`;
        setError(errorMsg);
        setShowErrorModal(true);
        return { found: false, rawResponse: response.rawContent, parsedResult: { jsonData, result } };
      }

      // Show the AI response for debugging
      clog('AI Validation Response:', jsonData);
      clog('AI Raw Response:', response.rawContent);
      
      if (!jsonData.tripLocations.found) {
        const errorMsg = `No locations found in destination.\n\nAI Response: ${JSON.stringify(jsonData, null, 2)}\n\nRaw Response: ${response.rawContent}`;
        setError(errorMsg);
        setShowErrorModal(true);
      }

      return { found: jsonData.tripLocations.found, rawResponse: response.rawContent, parsedResult: { jsonData, result } };
    } catch (err) {
      cerror('Error validating destination:', err);
      setError(`Validation error: ${err.message}\n\nStack: ${err.stack}`);
      return { found: false, error: err.message };
    }
  };

  // Handle form submission
  // useAI: true = generate activities with AI, false = create empty plan
  const handleSubmit = async (e, useAI = true) => {
    e.preventDefault();
    
    // Validation
    const newErrors = {};
    
    if (!destination.trim()) {
      newErrors.destination = t('tripCreator.destinationRequired');
    }
    
    if (!overallDates.startDate) {
      newErrors.startDate = t('tripCreator.startDateRequired');
    }
    if (!overallDates.endDate) {
      newErrors.endDate = t('tripCreator.endDateRequired');
    }
    if (overallDates.startDate && overallDates.endDate && 
        new Date(overallDates.startDate) > new Date(overallDates.endDate)) {
      newErrors.endDate = t('tripCreator.endDateAfterStart');
    }
    
    setErrors(newErrors);
    setError('');

    // Show validation errors in modal if any
    if (Object.keys(newErrors).length > 0) {
      const errorMessages = Object.values(newErrors).filter(msg => msg);
      if (errorMessages.length > 0) {
        setError(errorMessages.join('\n'));
        setAlertVariant('info');
        setShowErrorModal(true);
        return;
      }
    }

    if (Object.keys(newErrors).length === 0) {
      setIsLoading(true);
      setLoadingButton(useAI ? 'ai' : 'empty');
      setCurrentStep(t('tripCreator.checkingLimits'));
      
      try {
        // CRITICAL: Check plan limits BEFORE any AI processing!
        const limitsResponse = await apiCall(
          `/api/get?action=${ApiActions.CHECK_USAGE_LIMITS}&startDate=${encodeURIComponent(overallDates.startDate)}&endDate=${encodeURIComponent(overallDates.endDate)}`,
          {},
          'GET'
        );
        
        // Server-side validation - if it fails, show info and stop BEFORE AI processing
        if (!limitsResponse.success) {
          let errorMsg = 'Failed to check usage limits. Please try again.';
          
          if (limitsResponse.error === 'DAYS_LIMIT_EXCEEDED') {
            errorMsg = t('tripCreator.upgrade.daysLimitExceeded');
          } else if (limitsResponse.error === 'PLAN_LIMIT_REACHED') {
            errorMsg = t('tripCreator.upgrade.tripLimitReached');
          }
          
          setError(errorMsg);
          setAlertVariant('info');
          setShowErrorModal(true);
          setIsLoading(false);
          setLoadingButton(null);
          setCurrentStep('');
          return;
        }
        
        // Store plansCreated for redirect logic (check if first plan)
        const plansCreatedBeforeCreation = limitsResponse.plansCreated || 0;
        
        // Validate destination
        setCurrentStep(t('tripCreator.validatingDestination'));
        const validationResult = await validateDestination(destination);
        if (!validationResult.found) {
          // Error message already set in validateDestination with AI response details
          setIsLoading(false);
          setLoadingButton(null);
          setCurrentStep('');
          return;
        }

        // Build user input string
        const groupInfoStr = buildGroupInfoString();
        const interestsStr = buildInterestsString();
        
        // Convert dates to ISO format
        const startDateISO = new Date(overallDates.startDate).toISOString();
        const endDateISO = new Date(overallDates.endDate).toISOString();
        
        // Build prompt for AI
        let userPrompt = `Destination: ${destination}\n`;
        userPrompt += `Dates: ${overallDates.startDate} to ${overallDates.endDate}\n`;
        userPrompt += `Group: ${groupInfoStr}\n`;
        if (interestsStr) {
          userPrompt += `${interestsStr}\n`;
        }
        
        // Call AI with basicPlan template
        setCurrentStep(t('tripCreator.creatingTripStructure'));
        const basicPlanResponse = await apiCall('/api/rawAi', {
          prompt: userPrompt,
          context: createTripTemplatesV2.getBasicPlan(i18n.language),
          systemContent: createTripTemplatesV2.systemContext.getTripPlannerAssistant(i18n.language),
          feature: 'create_trip_empty_plan',
          language: i18n.language
        }, 'POST');

        if (!basicPlanResponse.success) {
          throw new Error(basicPlanResponse.message || 'Failed to generate plan');
        }

        // Parse AI response
        const { jsonData, result } = tryParseJson(basicPlanResponse.rawContent);
        if (!result || !jsonData) {
          throw new Error('Invalid response from AI');
        }

        // Ensure dates are in ISO format
        jsonData.startDate = startDateISO;
        jsonData.endDate = endDateISO;
        
        // Build groupInfo object from form data
        const totalPeople = groupMembers.adults + groupMembers.kids + groupMembers.babies + groupMembers.seniors;
        jsonData.groupInfo = {
          numberOfPeople: totalPeople || 1,
          ages: groupInfoStr
        };

        // Add interests to plan data (always include, even if empty)
        jsonData.interests = selectedInterests || [];

        // Get suggested attractions (for both empty and AI plans)
        setCurrentStep(t('tripCreator.findingAttractions'));
        let suggestedAttractions = [];
        try {
          const attractionsResponse = await apiCall('/api/rawAi', {
            prompt: userPrompt,
            context: createTripTemplatesV2.getAttractions(i18n.language),
            systemContent: createTripTemplatesV2.systemContext.getTripPlannerAssistant(i18n.language),
            feature: 'create_trip_suggested_attractions'
          }, 'POST');

          if (attractionsResponse.success) {
            const { jsonData: attractionsData, result: attractionsResult } = tryParseJson(attractionsResponse.rawContent);
            if (attractionsResult && attractionsData && attractionsData.suggestedAttractions) {
              suggestedAttractions = attractionsData.suggestedAttractions || [];
              clog('Parsed suggested attractions:', suggestedAttractions);
            }
          }
        } catch (attractionsErr) {
          cwarn('Error getting suggested attractions:', attractionsErr);
          // Don't fail the whole process if attractions fail
        }

        // Fetch images for suggested attractions
        if (suggestedAttractions.length > 0) {
          setCurrentStep(t('tripCreator.loadingAttractionImages'));
          try {
            const enhancedAttractions = await fetchAttractionImages(suggestedAttractions);
            suggestedAttractions = enhancedAttractions;
            clog('Enhanced suggested attractions with images:', suggestedAttractions);
          } catch (imageError) {
            cerror('Error fetching images for attractions:', imageError);
            // Don't fail the whole process if image fetching fails
          }
        }

        // Add suggested attractions to the plan data
        jsonData.suggestedAttractions = suggestedAttractions || [];

        // If creating plan with AI, generate attractions per day
        let userAttractions = [];
        if (useAI) {
          setCurrentStep(t('tripCreator.generatingItinerary'));
          try {
            const fullItineraryResponse = await apiCall('/api/rawAi', {
              prompt: userPrompt,
              context: createTripTemplatesV2.getFullItinerary(i18n.language),
              systemContent: createTripTemplatesV2.systemContext.getTripPlannerAssistant(i18n.language),
              feature: 'create_trip_full_itinerary',
              language: i18n.language
            }, 'POST');

            if (fullItineraryResponse.success) {
              const { jsonData: itineraryData, result: itineraryResult } = tryParseJson(fullItineraryResponse.rawContent);
              if (itineraryResult && itineraryData && itineraryData.userAttractions) {
                userAttractions = itineraryData.userAttractions || [];
                clog('Parsed user attractions for days:', userAttractions);
              }
            }
          } catch (itineraryErr) {
            cwarn('Error getting full itinerary:', itineraryErr);
            // Don't fail the whole process if itinerary generation fails
          }

          // Fetch images for user attractions
          if (userAttractions.length > 0) {
            setCurrentStep(t('tripCreator.loadingActivityImages'));
            try {
              const enhancedUserAttractions = await fetchAttractionImages(userAttractions);
              userAttractions = enhancedUserAttractions;
              clog('Enhanced user attractions with images:', userAttractions);
            } catch (imageError) {
              cerror('Error fetching images for user attractions:', imageError);
              // Don't fail the whole process if image fetching fails
            }
          }

          // Add userAttractions to jsonData - convertAiResultToPlan will add them to days
          jsonData.userAttractions = userAttractions || [];
        }

        // Get cover images from top 5 attractions (randomly choose 2)
        // Keep Image objects (not strings!) to preserve provider and attribution info
        const topAttractions = suggestedAttractions.slice(0, 5);
        const allImages = [];
        
        topAttractions.forEach(attraction => {
          if (attraction.images && Array.isArray(attraction.images)) {
            attraction.images.forEach(image => {
              // Keep the full Image object, not just the URL string
              if (image) {
                if (typeof image === 'string') {
                  // If it's already a string (legacy data), convert to Image object
                  allImages.push({
                    url: image,
                    sourceUrl: '',
                    provider: '',
                    attribution: null
                  });
                } else if (image.url) {
                  // It's already an Image object, keep it as is
                  allImages.push(image);
                }
              }
            });
          }
        });
        
        // Randomly choose 2 images
        if (allImages.length > 0) {
          const shuffled = allImages.sort(() => 0.5 - Math.random());
          jsonData.images = shuffled.slice(0, 2);
          clog('Selected cover images from top attractions:', jsonData.images);
        }

        // Convert to plan structure (creates empty days)
        const planData = convertAiResultToPlan(jsonData, null);
        
        // Save the plan to the database
        setCurrentStep(t('tripCreator.savingPlan'));
        const saveResponse = await apiCall("/api/model?action=" + ApiActions.CREATE_PLAN, {
          name: planData.name || "Trip Plan",
          data: JSON.stringify(planData),
          coverImage: planData.images && planData.images.length > 0 ? 
            (typeof planData.images[0] === 'string' ? planData.images[0] : planData.images[0].url) : '',
          startDate: planData.startDate || null,
          endDate: planData.endDate || null,
          language: i18n.language,
          userInput: {
            destination: destination,
            dates: {
              startDate: overallDates.startDate,
              endDate: overallDates.endDate
            },
            groupMembers: groupMembers,
            interests: selectedInterests,
            buttonClicked: useAI ? 'ai' : 'empty'
          }
        }, 'POST');
        
        if (saveResponse.success) {
          // Refresh account cache after successful plan creation (plansCreated was incremented)
          refreshAccountData().catch(err => console.error('Error refreshing account cache:', err));
          
          // If this is the first plan (plansCreated was 0), redirect to overview tab
          // Otherwise, redirect to days tab (default)
          const isFirstPlan = plansCreatedBeforeCreation === 0;
          const targetUrl = isFirstPlan 
            ? `/plan-detail/${saveResponse.plan.id}?tab=overview` 
            : `/plan-detail/${saveResponse.plan.id}`;
          navigate(targetUrl);
        } else {
          throw new Error(saveResponse.message || "Failed to save the plan.");
        }
        
      } catch (err) {
        cerror('Error creating plan:', err);
        setError(err.message || 'An error occurred while creating the plan');
        setShowErrorModal(true);
      } finally {
        setIsLoading(false);
        setLoadingButton(null);
        setCurrentStep('');
      }
    }
  };

  return (
    <div className="app-container">
      <SlidingMenu title={t('navigation.newTrip')} />
      <Container className="py-4">
        {/* Language Disclaimer Banner */}
        {showLanguageDisclaimer && (
          <Alert variant="info" dismissible onClose={handleDismissLanguageDisclaimer} className="mb-4">
            <Alert.Heading>
              <i className="bi bi-info-circle me-2"></i>
              {t('tripCreator.languageDisclaimer')}
            </Alert.Heading>
            <hr />
            <p className="mb-0 small text-muted" dir="ltr">
              <em>We use AI to translate this site. Some translations may sound unnatural, and we apologize for any awkward phrasing. Regardless of language, this site is intended for all genders.</em>
            </p>
          </Alert>
        )}
        <Card className="shadow-sm">
              <Card.Body className="p-4">
                <p className="text-muted small mb-3">
                  {t('tripCreator.haveSharedItinerary', { defaultValue: 'Have a shared itinerary?' })}{' '}
                  <Link to="/copy-itinerary">
                    <i className="bi bi-copy me-1"></i>
                    {t('navigation.copyItinerary', { defaultValue: 'Copy Itinerary' })}
                  </Link>
                </p>
                <Form onSubmit={handleSubmit}>
                  {/* Destination Section */}
                  <div className="mb-4">
                    <Form.Label className="fw-bold fs-5 mb-2">
                      <i className="bi bi-geo-alt me-2"></i>
                      {t('tripCreator.destination')} <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Text className="text-muted d-block mb-2">
                      {t('tripCreator.destinationHelp')}
                    </Form.Text>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder={t('tripCreator.destinationPlaceholder')}
                      value={destination}
                      onChange={(e) => setDestination(e.target.value)}
                      isInvalid={!!errors.destination}
                    />
                    {errors.destination && (
                      <Form.Control.Feedback type="invalid">
                        {errors.destination}
                      </Form.Control.Feedback>
                    )}
                  </div>

                  {/* Dates Section */}
                  <div className="mb-4">
                    <Form.Label className="fw-bold fs-5 mb-3">
                      <i className="bi bi-calendar-event me-2"></i>
                      {t('tripCreator.dates')} <span className="text-danger">*</span>
                    </Form.Label>
                    <Row>
                      <Col xs={12} sm={6} md={4} lg={2} xl={2} className="mb-3">
                        <Form.Label>{t('tripCreator.startDate')}</Form.Label>
                        <Form.Control
                          type="date"
                          value={overallDates.startDate}
                          onChange={(e) => setOverallDates(prev => ({ ...prev, startDate: e.target.value }))}
                          isInvalid={!!errors.startDate}
                        />
                        {errors.startDate && (
                          <Form.Control.Feedback type="invalid">
                            {errors.startDate}
                          </Form.Control.Feedback>
                        )}
                      </Col>
                      <Col xs={12} sm={6} md={4} lg={2} xl={2} className="mb-3">
                        <Form.Label>{t('tripCreator.endDate')}</Form.Label>
                        <Form.Control
                          type="date"
                          value={overallDates.endDate}
                          onChange={(e) => setOverallDates(prev => ({ ...prev, endDate: e.target.value }))}
                          min={overallDates.startDate}
                          isInvalid={!!errors.endDate}
                        />
                        {errors.endDate && (
                          <Form.Control.Feedback type="invalid">
                            {errors.endDate}
                          </Form.Control.Feedback>
                        )}
                      </Col>
                    </Row>
                  </div>

                  {/* Group Members Section */}
                  <div className="mb-4">
                    <Form.Label className="fw-bold fs-5 mb-3">
                      <i className="bi bi-people me-2"></i>
                      {t('tripCreator.groupMembers')}
                    </Form.Label>
                    <Row>
                      <Col xs={6} sm={4} md={3} lg={2} xl={2} className="mb-3">
                        <Form.Label>{t('tripCreator.adults')}</Form.Label>
                        <Form.Control
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={groupMembers.adults || ''}
                          onChange={(e) => handleGroupMemberChange('adults', e.target.value)}
                          placeholder="0"
                        />
                      </Col>
                      <Col xs={6} sm={4} md={3} lg={2} xl={2} className="mb-3">
                        <Form.Label>{t('tripCreator.kids')}</Form.Label>
                        <Form.Control
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={groupMembers.kids || ''}
                          onChange={(e) => handleGroupMemberChange('kids', e.target.value)}
                          placeholder="0"
                        />
                      </Col>
                      <Col xs={6} sm={4} md={3} lg={2} xl={2} className="mb-3">
                        <Form.Label>{t('tripCreator.babies')}</Form.Label>
                        <Form.Control
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={groupMembers.babies || ''}
                          onChange={(e) => handleGroupMemberChange('babies', e.target.value)}
                          placeholder="0"
                        />
                      </Col>
                      <Col xs={6} sm={4} md={3} lg={2} xl={2} className="mb-3">
                        <Form.Label>{t('tripCreator.seniors')}</Form.Label>
                        <Form.Control
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={groupMembers.seniors || ''}
                          onChange={(e) => handleGroupMemberChange('seniors', e.target.value)}
                          placeholder="0"
                        />
                      </Col>
                    </Row>
                  </div>

                  {/* Interests Section */}
                  <div className="mb-4">
                    <Form.Label className="fw-bold fs-5 mb-3">
                      <i className="bi bi-star me-2"></i>
                      {t('tripCreator.interests')}
                    </Form.Label>
                    <Form.Text className="text-muted d-block mb-3">
                      {t('tripCreator.interestsHelp')}
                    </Form.Text>
                    <div className="d-flex flex-wrap gap-2">
                      {INTERESTS.map(interest => (
                        <Button
                          key={interest.id}
                          variant={selectedInterests.includes(interest.id) ? 'primary' : 'outline-primary'}
                          size="sm"
                          onClick={() => toggleInterest(interest.id)}
                          className="mb-2 interest-button"
                        >
                          <i className={`bi ${interest.icon} me-1`}></i>
                          {t(`tripCreator.${interest.translationKey}`)}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Info Section */}
                  <div className="mb-3 mt-4 pt-3 border-top">
                    <div className="bg-light p-3 rounded">
                      <p className="mb-2 small text-muted">
                        <i className="bi bi-info-circle me-2"></i>
                        <strong>{t('tripCreator.chooseCreationMethod')}</strong>
                      </p>
                      <ul className="mb-0 small text-muted">
                        <li><strong>{t('tripCreator.createEmptyPlan')}:</strong> {t('tripCreator.emptyPlanDescription')}</li>
                        <li><strong>{t('tripCreator.createPlanWithAI')}:</strong> {t('tripCreator.aiPlanDescription')}</li>
                        <li className="mt-2"><em>{t('tripCreator.note')}</em></li>
                      </ul>
                    </div>
                  </div>

                  {/* Submit Buttons */}
                  <div className="d-flex justify-content-center gap-2">
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={(e) => {
                        e.preventDefault();
                        handleSubmit(e, false); // false = empty plan
                      }}
                      disabled={isLoading}
                    >
                      {loadingButton === 'empty' ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          {t('tripCreator.creating')}
                        </>
                      ) : (
                        <>
                          <i className="bi bi-calendar-plus me-2"></i>
                          {t('tripCreator.createEmptyPlan')}
                        </>
                      )}
                    </Button>
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={(e) => {
                        e.preventDefault();
                        handleSubmit(e, true); // true = with AI
                      }}
                      disabled={isLoading}
                    >
                      {loadingButton === 'ai' ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          {t('tripCreator.creating')}
                        </>
                      ) : (
                        <>
                          <i className="bi bi-magic me-2"></i>
                          {t('tripCreator.createPlanWithAI')}
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Progress Message */}
                  {isLoading && currentStep && (
                    <div className="mt-3 text-center">
                      <div className="text-primary fw-medium">
                        {currentStep}
                      </div>
                      <small className="text-muted d-block mt-2">
                        {t('tripCreator.mayTakeMinute')}
                      </small>
                    </div>
                  )}
                </Form>
          </Card.Body>
        </Card>
      </Container>
      <VersionFooter />

      {/* Alert Modal */}
      <AlertModal
        show={showErrorModal && !!error}
        onHide={() => {
          setError('');
          setShowErrorModal(false);
          setAlertVariant('error'); // Reset to default
        }}
        message={error}
        variant={alertVariant}
      />
    </div>
  );
}
